import React, { useState, useEffect } from 'react';

const TimePicker = ({ isOpen, onClose, onSelect, initialTime = '' }) => {
  const [selectedHour, setSelectedHour] = useState(5);
  const [selectedMinute, setSelectedMinute] = useState(44);
  const [period, setPeriod] = useState('AM');
  const [isSelectingMinute, setIsSelectingMinute] = useState(false);

  useEffect(() => {
    if (initialTime) {
      const [time, periodPart] = initialTime?.split(' ');
      const [hours, minutes] = time?.split(':');
      setSelectedHour(parseInt(hours) || 5);
      setSelectedMinute(parseInt(minutes) || 0);
      setPeriod(periodPart || 'AM');
    }
  }, [initialTime]);

  const hours = Array.from({ length: 12 }, (_, i) => i + 1);
  const minutes = Array.from({ length: 12 }, (_, i) => i * 5);

  const getClockPosition = (value, total, radius) => {
    const angle = ((value - 3) * (360 / total) * Math.PI) / 180;
    return {
      x: radius * Math.cos(angle),
      y: radius * Math.sin(angle)
    };
  };

  const handleClockClick = (e) => {
    const rect = e?.currentTarget?.getBoundingClientRect();
    const centerX = rect?.width / 2;
    const centerY = rect?.height / 2;
    const x = e?.clientX - rect?.left - centerX;
    const y = e?.clientY - rect?.top - centerY;
    const angle = Math.atan2(y, x);
    const degrees = (angle * 180) / Math.PI;
    const normalizedDegrees = (degrees + 360 + 90) % 360;

    if (isSelectingMinute) {
      const minuteValue = Math.round(normalizedDegrees / 30) * 5;
      setSelectedMinute(minuteValue === 60 ? 0 : minuteValue);
    } else {
      const hourValue = Math.round(normalizedDegrees / 30);
      setSelectedHour(hourValue === 0 ? 12 : hourValue);
    }
  };

  const handleConfirm = () => {
    const formattedTime = `${String(selectedHour)?.padStart(2, '0')}:${String(selectedMinute)?.padStart(2, '0')} ${period}`;
    onSelect?.(formattedTime);
    onClose?.();
  };

  if (!isOpen) return null;

  const clockRadius = 120;
  const currentValue = isSelectingMinute ? selectedMinute / 5 : selectedHour;
  const totalValues = 12;
  const handPosition = getClockPosition(currentValue, totalValues, clockRadius - 30);

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={onClose}>
      <div className="bg-white rounded-lg shadow-2xl w-[340px] overflow-hidden" onClick={(e) => e?.stopPropagation()}>
        {/* Header with Time Display */}
        <div className="bg-blue-500 text-white p-6 text-center">
          <div className="flex items-center justify-center gap-4">
            <button
              onClick={() => setIsSelectingMinute(false)}
              className={`text-6xl font-light transition-opacity ${
                !isSelectingMinute ? 'opacity-100' : 'opacity-50'
              }`}
            >
              {String(selectedHour)?.padStart(2, '0')}
            </button>
            <span className="text-6xl font-light">:</span>
            <button
              onClick={() => setIsSelectingMinute(true)}
              className={`text-6xl font-light transition-opacity ${
                isSelectingMinute ? 'opacity-100' : 'opacity-50'
              }`}
            >
              {String(selectedMinute)?.padStart(2, '0')}
            </button>
          </div>
          <div className="flex justify-center gap-4 mt-2">
            <button
              onClick={() => setPeriod('AM')}
              className={`text-lg font-medium px-3 py-1 rounded transition-colors ${
                period === 'AM' ? 'bg-white/20' : 'opacity-60 hover:opacity-100'
              }`}
            >
              AM
            </button>
            <button
              onClick={() => setPeriod('PM')}
              className={`text-lg font-medium px-3 py-1 rounded transition-colors ${
                period === 'PM' ? 'bg-white/20' : 'opacity-60 hover:opacity-100'
              }`}
            >
              PM
            </button>
          </div>
        </div>

        {/* Clock Face */}
        <div className="p-8 bg-gray-50">
          <div
            className="relative w-64 h-64 mx-auto bg-white rounded-full shadow-inner cursor-pointer"
            onClick={handleClockClick}
          >
            {/* Clock Numbers */}
            {(isSelectingMinute ? minutes : hours)?.map((value, index) => {
              const displayValue = isSelectingMinute ? value : value;
              const position = getClockPosition(isSelectingMinute ? value / 5 : value, 12, clockRadius - 20);
              return (
                <div
                  key={index}
                  className="absolute transform -translate-x-1/2 -translate-y-1/2 text-gray-700 font-medium text-sm"
                  style={{
                    left: `calc(50% + ${position?.x}px)`,
                    top: `calc(50% + ${position?.y}px)`
                  }}
                >
                  {displayValue === 0 ? (isSelectingMinute ? '00' : '12') : String(displayValue)?.padStart(2, '0')}
                </div>
              );
            })}

            {/* Clock Hand */}
            <div
              className="absolute top-1/2 left-1/2 origin-left transform -translate-y-1/2"
              style={{
                width: `${Math.sqrt(handPosition?.x ** 2 + handPosition?.y ** 2)}px`,
                transform: `translate(-50%, -50%) rotate(${Math.atan2(handPosition?.y, handPosition?.x) * (180 / Math.PI)}deg)`,
                transformOrigin: 'left center'
              }}
            >
              <div className="h-0.5 bg-blue-500 w-full"></div>
            </div>

            {/* Center Dot */}
            <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-3 h-3 bg-blue-500 rounded-full"></div>

            {/* Selected Value Indicator */}
            <div
              className="absolute transform -translate-x-1/2 -translate-y-1/2 w-10 h-10 bg-blue-500 rounded-full flex items-center justify-center text-white font-bold shadow-lg"
              style={{
                left: `calc(50% + ${handPosition?.x}px)`,
                top: `calc(50% + ${handPosition?.y}px)`
              }}
            >
              {isSelectingMinute ? String(selectedMinute)?.padStart(2, '0') : selectedHour}
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex justify-end gap-4 p-4 bg-white">
          <button
            onClick={onClose}
            className="text-blue-500 font-semibold px-6 py-2 hover:bg-blue-50 rounded transition-colors"
          >
            CANCEL
          </button>
          <button
            onClick={handleConfirm}
            className="text-blue-500 font-semibold px-6 py-2 hover:bg-blue-50 rounded transition-colors"
          >
            OK
          </button>
        </div>
      </div>
    </div>
  );
};

export default TimePicker;